/**
 * The toast contract, kept apart from the components that implement it so a
 * module can raise a toast without importing the viewport, and so the hook and
 * the provider can live in files that each export one kind of thing.
 */

import { createContext, useContext } from 'react'

export type ToastTone = 'info' | 'success' | 'warning' | 'danger'

export interface ToastAction {
  label: string
  onClick(): void
}

export interface ToastOptions {
  /** One line, in the past tense, saying what happened. */
  title: string
  /** The detail, if the title cannot carry it alone. */
  description?: string
  tone?: ToastTone
  /**
   * One action, and only ever an action that recovers from what just happened:
   * Undo, View, Retry. A toast is on screen for six seconds, so an action here
   * that is merely useful is an action most people will never reach.
   */
  action?: ToastAction
  /**
   * Milliseconds on screen. `0` keeps it until it is dismissed.
   *
   * Leave it unset. The defaults are set per tone in the provider and they are
   * the considered ones.
   */
  duration?: number
}

export interface ToastRecord extends ToastOptions {
  id: string
  tone: ToastTone
  duration: number
}

export interface ToastApi {
  /** Returns the id, so a long-running outcome can be dismissed when it lands. */
  show(options: ToastOptions): string
  dismiss(id: string): void
  clear(): void
}

export const ToastContext = createContext<ToastApi | null>(null)

/**
 * Raises a toast.
 *
 * The rule that governs every call site: **a toast is never the only place an
 * outcome is reported.** It is a transient, and a transient is missed by anyone
 * who was looking at the other half of the screen, anyone whose attention was
 * pulled away by the thing they just did, and anyone using a screen reader who
 * was mid-sentence when it arrived. The durable report is the table row that
 * changed, the status that now reads "Published", the banner on the record, the
 * error rendered next to the field that caused it. The toast tells someone who
 * is watching that it worked; it does not tell anyone else anything.
 */
export function useToast(): ToastApi {
  const api = useContext(ToastContext)
  if (!api) {
    throw new Error('useToast requires a <ToastProvider> above it in the tree.')
  }
  return api
}
